import { useState, useEffect, useCallback, useMemo, createContext, useContext, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';
import { createDemoAwareClient } from '@/integrations/supabase/client';
import { apiFetch } from '@/lib/api/requests';
import { computeDemoKpis, subtotalAndTaxCents, type DemoOrder, type DemoOrderItem } from '@/lib/kpis/demoKpis';

export interface DemoContextType {
  // Demo state
  isDemo: boolean;
  demoRestaurantId: string | null;
  slug: string | null;
  demoJWT: string | null;
  demoToken: string | null;
  expiresAt: string | null;
  restaurantName: string | null;

  // Status
  isLoading: boolean;
  timeLeft: number;
  timeLeftFormatted: string;
  isExpired: boolean;

  // Demo data (in-memory exposure)
  orders: DemoOrder[];
  menuItems: DemoMenuItem[];
  ready: boolean;

  // KPIs derived from orders
  orderCount: number;
  todayOrdersCount: number;
  revenueCents: number;
  kitchenQueue: number;
  demoTables: Array<{ id: string; number: string; qr_slug?: string }>;

  // Actions
  createDemo: (name: string, desiredSlug: string, locationName?: string) => Promise<{ success: boolean; data?: Record<string, unknown>; error?: string }>;
  endDemo: () => Promise<void>;
  refreshDemo: () => Promise<void>;
  updateDemoOrderStatus: (orderId: string, newStatus: string) => Promise<void>;
  createCounterOrder: (input: {
    tableNumber: string;
    items: Array<{ id?: string; name: string; price_cents?: number; qty: number; modifiers?: Array<{ name: string; price_delta_cents?: number }> }>;
    guests?: number;
    notes?: string;
  }) => Promise<{ id: string; code: string }>;

  // QR codes
  sampleQRs: DemoQR[];
}

export interface DemoQR {
  table_id?: string;
  table_number?: string;
  qr_slug?: string;
  url?: string;
}

export interface DemoMenuItem {
  id: string;
  name: string;
  description?: string;
  price_snapshot_cents?: number;
  price_cents?: number;
  price?: number;
  category?: string;
  is_available?: boolean;
  prep_minutes?: number;
  rating?: number;
}

interface SeedMenuItem {
  id: string;
  name: string;
  price_snapshot_cents: number;
}

// Demo context
export const DemoContext = createContext<DemoContextType | null>(null);

const DEMO_STORAGE_KEYS = {
  JWT: 'braise:demo_jwt_v2',
  RESTAURANT_ID: 'braise:demo_restaurant_id_v2',
  SLUG: 'braise:demo_slug_v2',
  EXPIRES_AT: 'braise:demo_expires_at_v2',
  RESTAURANT_NAME: 'braise:demo_restaurant_name_v2',
  SAMPLE_QRS: 'braise:demo_sample_qrs_v2',
} as const;

const isBrowser = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

// Demo orders/session keys
const ORDERS_V2 = 'braise:demo_orders_v2';
const ORDERS_LEGACY = 'braise:demo_orders';
const SESSION_ID = 'braise:demo_session_id_v2';
const SEEDED_FLAG = 'braise:demo_seeded_v2';

function parseJSON<T>(raw: string | null, label: string): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch (error) {
    logDevError(`failed to parse ${label}`, error);
    return null;
  }
}

function logDevError(context: string, error: unknown): void {
  if (import.meta.env?.DEV) {
    console.debug(`[useDemoV2] ${context}`, error);
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    logDevError(`remove ${key} failed`, error);
  }
}

function safeSet(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    logDevError(`set ${key} failed`, error);
  }
}

function persistString(key: string, value: string | null | undefined): void {
  if (!value || value.length === 0) {
    safeRemove(key);
    return;
  }
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    logDevError(`set ${key} failed`, error);
  }
}

const DEMO_PROVISION_ENDPOINT = '/demo/provision';

interface ProvisionedDemoSession {
  demo_jwt: string;
  demo_restaurant_id: string;
  slug: string;
  expires_at: string;
  name: string;
  sample_qrs?: DemoQR[];
}

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const DEMO_STORAGE_KEYS_TO_CLEAR = [
  DEMO_STORAGE_KEYS.JWT,
  DEMO_STORAGE_KEYS.RESTAURANT_ID,
  DEMO_STORAGE_KEYS.SLUG,
  DEMO_STORAGE_KEYS.EXPIRES_AT,
  DEMO_STORAGE_KEYS.RESTAURANT_NAME,
  DEMO_STORAGE_KEYS.SAMPLE_QRS,
  ORDERS_V2,
  ORDERS_LEGACY,
  'braise:demo_menu_items_v2',
  'braise:demo_menu_items',
  SESSION_ID,
  SEEDED_FLAG,
];

const normalizeSampleQRs = (value: unknown): DemoQR[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const list: DemoQR[] = [];
  value.forEach((entry) => {
    if (!entry || typeof entry !== 'object') {
      return;
    }
    const record = entry as Record<string, unknown>;
    const tableId =
      typeof record.table_id === 'string'
        ? record.table_id
        : typeof record.id === 'string'
          ? record.id
          : undefined;
    const tableNumber =
      typeof record.table_number === 'string'
        ? record.table_number
        : typeof record.number === 'string'
          ? record.number
          : undefined;
    const qrSlug =
      typeof record.qr_slug === 'string'
        ? record.qr_slug
        : typeof record.slug === 'string'
          ? record.slug
          : undefined;
    const url = typeof record.url === 'string' ? record.url : undefined;

    list.push({
      table_id: tableId,
      table_number: tableNumber,
      qr_slug: qrSlug,
      url,
    });
  });

  return list.length > 0 ? list : undefined;
};

const normalizeProvisionPayload = (payload: unknown): ProvisionedDemoSession | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const container = payload as Record<string, unknown>;
  const data =
    container.data && typeof container.data === 'object'
      ? (container.data as Record<string, unknown>)
      : container;

  const pick = (...keys: string[]): string | null => {
    for (const key of keys) {
      const value = data[key];
      if (isNonEmptyString(value)) {
        return value.trim();
      }
    }
    return null;
  };

  const demoJwt = pick('demo_jwt', 'demoToken', 'demo_token', 'token');
  const restaurantId = pick('demo_restaurant_id', 'restaurant_id', 'tenant_id');
  const slug = pick('slug', 'subdomain', 'tenant_slug');
  const expiresAt = pick('expires_at', 'expiry', 'expiresAt');
  const name = pick('name', 'restaurant_name') ?? 'Demo Restaurant';
  const sampleQrs = normalizeSampleQRs(
    'sample_qrs' in data
      ? (data.sample_qrs as unknown)
      : 'sampleQrs' in data
        ? (data.sampleQrs as unknown)
        : undefined,
  );

  if (!demoJwt || !restaurantId || !slug || !expiresAt) {
    return null;
  }

  return {
    demo_jwt: demoJwt,
    demo_restaurant_id: restaurantId,
    slug,
    expires_at: expiresAt,
    name,
    sample_qrs: sampleQrs,
  };
};

const deriveProvisionErrorMessage = (payload: unknown): string | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const container = payload as Record<string, unknown>;
  const errorValue = container.error;

  if (errorValue && typeof errorValue === 'object') {
    const errorRecord = errorValue as Record<string, unknown>;
    const message = errorRecord.message;
    if (isNonEmptyString(message)) {
      return message.trim();
    }
    const code = errorRecord.code;
    if (isNonEmptyString(code)) {
      return code.trim();
    }
  }

  const message = container.message;
  if (isNonEmptyString(message)) {
    return message.trim();
  }

  if (Array.isArray(container.errors)) {
    const stringError = container.errors.find((item) => isNonEmptyString(item)) as string | undefined;
    if (stringError) {
      return stringError.trim();
    }
  }

  return null;
};

const buildDefaultSampleQRs = (slug: string, count = 6): DemoQR[] => {
  if (!isNonEmptyString(slug)) {
    return [];
  }

  const trimmed = slug.trim();
  return Array.from({ length: count }, (_, index) => {
    const tableNumber = index + 1;
    const tableId = `table-${tableNumber}`;
    const qrSlug = `${trimmed}-table-${tableNumber}`;
    const url =
      typeof window !== 'undefined' && window.location
        ? `${window.location.origin}/r/${trimmed}/table/${qrSlug}?mode=demo`
        : undefined;
    return {
      table_id: tableId,
      table_number: String(tableNumber),
      qr_slug: qrSlug,
      url,
    };
  });
};

function ensureOrderTotals(orders: DemoOrder[]): { orders: DemoOrder[]; updated: number } {
  let updated = 0;
  const normalized = orders.map((order) => {
    if (!order) return order;
    const items = order.order_items || order.demo_order_items || order.items || [];
    const totalsMissing = (order.total_cents ?? 0) <= 0;
    if (!totalsMissing || !Array.isArray(items) || items.length === 0) {
      return order;
    }
    const totals = subtotalAndTaxCents(order);
    updated += 1;
    return {
      ...order,
      subtotal_cents: totals.subtotal_cents,
      tax_cents: totals.tax_cents,
      total_cents: totals.total_cents,
      total_amount: totals.total_cents / 100,
    };
  });
  return { orders: normalized, updated };
}

function persistOrders(orders: DemoOrder[]): void {
  safeSet(ORDERS_V2, orders);
  safeSet(ORDERS_LEGACY, orders);
}

export function useDemoV2(): DemoContextType {
  const ctx = useContext(DemoContext);
  if (!ctx) {
    return {
      isDemo: false,
      demoRestaurantId: null,
      slug: null,
      demoJWT: null,
      demoToken: null,
      expiresAt: null,
      restaurantName: null,
      isLoading: false,
      timeLeft: 0,
      timeLeftFormatted: '00:00',
      isExpired: false,
      orders: [],
      menuItems: [],
      ready: false,
      orderCount: 0,
      todayOrdersCount: 0,
      revenueCents: 0,
      kitchenQueue: 0,
      demoTables: [],
      createDemo: async () => ({ success: false, error: 'Demo provider not available' }),
      endDemo: async () => {},
      refreshDemo: async () => {},
      updateDemoOrderStatus: async () => {},
      createCounterOrder: async () => ({ id: '', code: '' }),
      sampleQRs: [],
    };
  }
  return ctx;
}

export function useDemoProvider(): DemoContextType {
  const { toast } = useToast();

  const [isDemo, setIsDemo] = useState(false);
  const [demoRestaurantId, setDemoRestaurantId] = useState<string | null>(null);
  const [slug, setSlug] = useState<string | null>(null);
  const [demoJWT, setDemoJWT] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [restaurantName, setRestaurantName] = useState<string | null>(null);
  const [sampleQRs, setSampleQRs] = useState<DemoQR[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [orders, setOrders] = useState<DemoOrder[]>([]);
  const [menuItems, setMenuItems] = useState<DemoMenuItem[]>([]);
  const [ready, setReady] = useState(false);
  const [dbDemoTables, setDbDemoTables] = useState<Array<{ id: string; table_number: string; qr_slug?: string }>>([]);
  const seededOnceRef = useRef(false);
  const ordersRef = useRef<DemoOrder[]>([]);
  const initialDbHandledRef = useRef(false);
  const keepSeedsRef = useRef(false);
  const topUpSeedsRef = useRef(false);
  const dbEmptyLoggedRef = useRef(false);
  const topUpLoggedRef = useRef(false);
  useEffect(() => { ordersRef.current = orders; }, [orders]);

  const timeLeftFormatted = useMemo(() => {
    const h = Math.floor(timeLeft / 3600);
    const m = Math.floor((timeLeft % 3600) / 60).toString().padStart(2, '0');
    const s = (timeLeft % 60).toString().padStart(2, '0');
    return h > 0 ? `${h}:${m}:${s}` : `${m}:${s}`;
  }, [timeLeft]);
  const isExpired = isDemo && timeLeft <= 0;

  const loadDemoSession = useCallback(() => {
    try {
      const jwt = localStorage.getItem(DEMO_STORAGE_KEYS.JWT);
      const restaurantId = localStorage.getItem(DEMO_STORAGE_KEYS.RESTAURANT_ID);
      const demoSlug = localStorage.getItem(DEMO_STORAGE_KEYS.SLUG);
      const expires = localStorage.getItem(DEMO_STORAGE_KEYS.EXPIRES_AT);
      const name = localStorage.getItem(DEMO_STORAGE_KEYS.RESTAURANT_NAME);
      const qrs = localStorage.getItem(DEMO_STORAGE_KEYS.SAMPLE_QRS);
      if (jwt && restaurantId && demoSlug && expires) {
        const expMs = new Date(expires).getTime();
        const now = Date.now();
        if (expMs > now) {
          setIsDemo(true);
          setDemoJWT(jwt);
          setDemoRestaurantId(restaurantId);
          setSlug(demoSlug);
          setExpiresAt(expires);
          setRestaurantName(name);
          const parsedQRs = parseJSON<DemoQR[]>(qrs, 'sample_qrs');
          if (parsedQRs) {
            setSampleQRs(parsedQRs);
          }
          setTimeLeft(Math.floor((expMs - now) / 1000));
        } else {
          setIsDemo(false);
        }
      } else {
        setIsDemo(false);
      }
    } catch (error) {
      if (import.meta.env?.DEV) {
        console.debug('[useDemoV2] loadDemoSession failed', error);
      }
      setIsDemo(false);
    }
  }, []);

  // Initialize session + timer
  useEffect(() => { loadDemoSession(); }, [loadDemoSession]);
  useEffect(() => {
    if (!isDemo && isBrowser) {
      DEMO_STORAGE_KEYS_TO_CLEAR.forEach((key) => {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          logDevError(`clear ${key} failed`, error);
        }
      });
    }
  }, [isDemo]);
  useEffect(() => {
    if (!isDemo || !expiresAt) return;
    const id = setInterval(() => {
      const expMs = new Date(expiresAt).getTime();
      const now = Date.now();
      setTimeLeft(Math.max(0, Math.floor((expMs - now) / 1000)));
    }, 1000);
    return () => clearInterval(id);
  }, [isDemo, expiresAt]);

  // Load orders/menu from LS and subscribe to updates
  useEffect(() => {
    try {
      const lsOrdersRaw = localStorage.getItem(ORDERS_V2) || localStorage.getItem(ORDERS_LEGACY);
      const lsMenuRaw = localStorage.getItem('braise:demo_menu_items_v2') || localStorage.getItem('braise:demo_menu_items');
      const parsedOrders = parseJSON<DemoOrder[]>(lsOrdersRaw, 'orders');
      if (parsedOrders) {
        const { orders: normalizedOrders, updated } = ensureOrderTotals(parsedOrders);
        if (updated > 0) {
          persistOrders(normalizedOrders);
          if (import.meta.env?.DEV) {
            console.debug('[revenue] backfilled totals for', { count: updated });
          }
          window.dispatchEvent(new CustomEvent('demoOrdersUpdated', { detail: { orders: normalizedOrders, meta: { source: 'backfill' } } }));
        }
        ordersRef.current = normalizedOrders;
        setOrders(normalizedOrders);
      }
      const parsedMenu = parseJSON<DemoMenuItem[]>(lsMenuRaw, 'menu');
      if (parsedMenu) {
        setMenuItems(parsedMenu);
      }
    } catch (error) {
      if (import.meta.env?.DEV) {
        console.debug('[useDemoV2] failed to hydrate from localStorage', error);
      }
    }
    setReady(true);

    const applyOrders = (list: DemoOrder[] | undefined) => {
      const baseList = Array.isArray(list) ? list : [];
      const { orders: normalizedOrders, updated } = ensureOrderTotals(baseList);
      if (updated > 0) {
        persistOrders(normalizedOrders);
        if (import.meta.env?.DEV) {
          console.debug('[revenue] backfilled totals for', { count: updated });
        }
        window.dispatchEvent(new CustomEvent('demoOrdersUpdated', { detail: { orders: normalizedOrders, meta: { source: 'backfill' } } }));
      } else {
        persistOrders(normalizedOrders);
      }
      ordersRef.current = normalizedOrders;
      setOrders(normalizedOrders);
      setReady(true);
      if (import.meta.env?.DEV) {
        console.debug('[orders] event -> state updated', { count: normalizedOrders.length });
      }
    };

    const onOrders = (event: Event) => {
      const detail = (event as CustomEvent<{ orders?: DemoOrder[]; meta?: { source?: string } }>).detail;
      if (detail?.meta?.source === 'backfill') {
        return;
      }
      const incomingRaw = detail?.orders;
      const seededActive = localStorage.getItem(SEEDED_FLAG) === '1';
      const incoming = Array.isArray(incomingRaw) ? incomingRaw : undefined;
      const incomingHasSeed = Array.isArray(incoming)
        ? incoming.some((order) => String(order?.id ?? '').startsWith('seed_'))
        : false;
      const isSeedPayload = seededActive && Array.isArray(incoming)
        ? incoming.length > 0 && incoming.every((order) => String(order?.id ?? '').startsWith('seed_'))
        : false;

      if (!initialDbHandledRef.current && seededActive && !isSeedPayload) {
        initialDbHandledRef.current = true;
        if (!incoming || incoming.length === 0) {
          keepSeedsRef.current = true;
        } else if (incoming.length < 7) {
          topUpSeedsRef.current = true;
          topUpLoggedRef.current = false;
        }
      }

      if (
        initialDbHandledRef.current &&
        seededActive &&
        !keepSeedsRef.current &&
        !topUpSeedsRef.current &&
        Array.isArray(incoming) &&
        incoming.length > 0 &&
        incoming.length < 7 &&
        !incomingHasSeed
      ) {
        topUpSeedsRef.current = true;
        topUpLoggedRef.current = false;
      }

      if (keepSeedsRef.current && seededActive) {
        if (!incoming || incoming.length === 0) {
          if (import.meta.env?.DEV && !dbEmptyLoggedRef.current) {
            console.debug('[demo] DB empty - keeping seeds');
            dbEmptyLoggedRef.current = true;
          }
          const snapshot = ordersRef.current || [];
          persistOrders(snapshot);
          applyOrders(snapshot);
          return;
        }
        keepSeedsRef.current = false;
        dbEmptyLoggedRef.current = false;
        if (incoming.length < 7) {
          topUpSeedsRef.current = true;
          topUpLoggedRef.current = false;
        }
      }

      if (topUpSeedsRef.current && seededActive) {
        if (!incoming || incoming.length < 7 || incomingHasSeed) {
          const base: DemoOrder[] = Array.isArray(incoming)
            ? incoming.filter((order) => !String(order?.id ?? '').startsWith('seed_'))
            : [];
          const seenCodes = new Set(base.map((order) => order.order_code ?? order.orderCode ?? order.id));
          let added = 0;
          const seedSource = (ordersRef.current || []).filter((order) => String(order?.id ?? '').startsWith('seed_'));
          for (const seed of seedSource) {
            const code = seed?.order_code || seed?.orderCode || seed?.id;
            if (!seenCodes.has(code)) {
              base.push(seed);
              seenCodes.add(code);
              added++;
            }
            if (base.length >= 7) break;
          }
          if (import.meta.env?.DEV && !topUpLoggedRef.current) {
            console.debug('[demo] DB <7 - topping up with', added, 'seeded');
            topUpLoggedRef.current = true;
          }
          applyOrders(base);
          return;
        }
        topUpSeedsRef.current = false;
        topUpLoggedRef.current = false;
      }

      applyOrders(Array.isArray(incoming) ? incoming : []);
    };
    const onMenu = (event: Event) => {
      const detail = (event as CustomEvent<{ menuItems?: DemoMenuItem[] }>).detail;
      if (Array.isArray(detail?.menuItems)) {
        setMenuItems(detail.menuItems);
      }
      setReady(true);
    };
    window.addEventListener('demoOrdersUpdated', onOrders);
    window.addEventListener('demoMenuUpdated', onMenu);
    return () => {
      window.removeEventListener('demoOrdersUpdated', onOrders);
      window.removeEventListener('demoMenuUpdated', onMenu);
    };
  }, []);

  // Helper: seed fallback demo orders (at least minCount)
  const seedFallbackOrders = useCallback((minCount: number = 7): DemoOrder[] => {
    try {
      const now = Date.now();
      const hasMenu = Array.isArray(menuItems) && menuItems.length > 0;
      const fallbackMenu: SeedMenuItem[] = [
        { id: 'item_1', name: 'Margherita Pizza', price_snapshot_cents: 1299 },
        { id: 'item_2', name: 'Lemonade', price_snapshot_cents: 350 },
        { id: 'item_3', name: 'Caesar Salad', price_snapshot_cents: 999 },
        { id: 'item_4', name: 'Garlic Bread', price_snapshot_cents: 499 },
      ];
      const sourceMenu: SeedMenuItem[] = hasMenu
        ? menuItems.map((item) => ({
            id: item.id,
            name: item.name,
            price_snapshot_cents:
              typeof item.price_snapshot_cents === 'number'
                ? item.price_snapshot_cents
                : typeof item.price_cents === 'number'
                ? item.price_cents
                : typeof item.price === 'number'
                ? Math.round(item.price * 100)
                : 0,
          }))
        : fallbackMenu;

      let tables: string[] = [];
      const qrsRaw = localStorage.getItem(DEMO_STORAGE_KEYS.SAMPLE_QRS);
      const parsedQrs = parseJSON<DemoQR[]>(qrsRaw, 'seed sample_qrs');
      if (parsedQrs) {
        tables = parsedQrs
          .map((qr) => qr.table_number || '')
          .filter((value): value is string => Boolean(value));
      }
      if (tables.length === 0) {
        tables = Array.from({ length: 12 }, (_, i) => String(i + 1));
      }

      const pickItems = (count: number): DemoOrderItem[] => {
        const selections: DemoOrderItem[] = [];
        for (let i = 0; i < count; i += 1) {
          const menuEntry = sourceMenu[(i + Math.floor(Math.random() * sourceMenu.length)) % sourceMenu.length];
          const qty = 1 + (i % 2 === 0 ? 0 : 1);
          selections.push({
            item_id: menuEntry.id,
            item_name: menuEntry.name,
            quantity: qty,
            price_snapshot_cents: menuEntry.price_snapshot_cents,
          });
        }
        return selections;
      };

      const statusPlan = ['COMPLETED', 'COMPLETED', 'READY', 'IN_PROGRESS', 'IN_PROGRESS', 'PAID', 'PENDING_PAYMENT'];
      while (statusPlan.length < minCount) {
        statusPlan.push('PENDING_PAYMENT');
      }

      const seeded: DemoOrder[] = statusPlan.slice(0, Math.max(minCount, 7)).map((status, idx) => {
        const placedAt = new Date(now - (idx + 2) * 5 * 60 * 1000).toISOString();
        const orderItems = pickItems(1 + (idx % 3));
        const totals = subtotalAndTaxCents(orderItems);
        const tableNumber = tables[idx % tables.length] || '1';
        const seededOrder: DemoOrder = {
          id: `seed_${now}_${idx}`,
          order_code: `SEED-${(now + idx).toString(36).toUpperCase()}`,
          status,
          table_number: tableNumber,
          items: orderItems,
          placed_at: placedAt,
          created_at: placedAt,
          updated_at: placedAt,
          payment_method: 'CARD_TEST',
          notes: '',
          subtotal_cents: totals.subtotal_cents,
          tax_cents: totals.tax_cents,
          total_cents: totals.total_cents,
          total_amount: totals.total_cents / 100,
        };
        return seededOrder;
      });

      persistOrders(seeded);
      localStorage.setItem(SEEDED_FLAG, '1');
      ordersRef.current = seeded;
      setOrders(seeded);
      window.dispatchEvent(new CustomEvent('demoOrdersUpdated', { detail: { orders: seeded } }));
      if (import.meta.env?.DEV && !seededOnceRef.current) {
        console.debug('[demo] seeded', seeded.length, 'orders');
        seededOnceRef.current = true;
      }
      return seeded;
    } catch (error) {
      logDevError('seedFallbackOrders failed', error);
      return [];
    }
  }, [menuItems]);

  // Attempt to load demo tables from DB (DB-first when available)
  useEffect(() => {
    (async () => {
      try {
        if (!isDemo || !demoRestaurantId) return;
        const client = createDemoAwareClient(demoJWT || undefined);
        const { data, error } = await client
          .from('demo_tables')
          .select('id, table_number, qr_slug')
          .eq('demo_restaurant_id', demoRestaurantId)
          .eq('is_active', true)
          .order('table_number');
        if (!error && Array.isArray(data)) {
          const mapped = data
            .map((row) => ({
              id: String(row.id),
              table_number: String(row.table_number),
              qr_slug: row.qr_slug ?? undefined,
            }))
            .filter((row) => row.id && row.table_number);
          setDbDemoTables(mapped);
        }
      } catch (error) {
        logDevError('load demo tables failed', error);
      }
    })();
  }, [isDemo, demoRestaurantId, demoJWT]);

  // New session detection + first-boot seeding
  useEffect(() => {
    try {
      if (!isDemo) return;
      const jwt = demoJWT || localStorage.getItem(DEMO_STORAGE_KEYS.JWT) || '';
      const restId = demoRestaurantId || localStorage.getItem(DEMO_STORAGE_KEYS.RESTAURANT_ID) || '';
      if (!jwt) return; // not ready
      const currentSessionId = restId ? `${restId}:${jwt}` : jwt;
      const prev = localStorage.getItem(SESSION_ID);
      if (prev !== currentSessionId) {
        // Brand-new demo session: clear demo order keys + seeded flag
        safeRemove(ORDERS_V2);
        safeRemove(ORDERS_LEGACY);
        safeRemove(SEEDED_FLAG);
        safeRemove(SESSION_ID);
        initialDbHandledRef.current = false;
        keepSeedsRef.current = false;
        topUpSeedsRef.current = false;
        dbEmptyLoggedRef.current = false;
        topUpLoggedRef.current = false;
        localStorage.setItem(SESSION_ID, currentSessionId);
        // Seed initial orders
        seedFallbackOrders(7);
      } else {
        // Same session: if already seeded, don't reseed
        const seeded = localStorage.getItem(SEEDED_FLAG) === '1';
        if (seeded) {
          if (import.meta.env?.DEV) console.debug('[useDemoV2] session unchanged; seeded flag present');
        }
      }
    } catch (error) {
      logDevError('session guard failed', error);
    }
  }, [isDemo, demoJWT, demoRestaurantId, seedFallbackOrders]);

  // KPI selectors
  const { orderCount, todayOrdersCount, kitchenQueueCount, revenueTodayCents } = useMemo(
    () => computeDemoKpis(orders),
    [orders]
  );
  const revenueCents = revenueTodayCents;
  const kitchenQueue = kitchenQueueCount;

  // Demo tables selector: sample_qrs -> DB -> fallback 12
  const [demoTablesLogged, setDemoTablesLogged] = useState(false);
  const demoTablesMemo = useMemo(() => {
    let src: 'sample_qrs' | 'db' | 'fallback' = 'fallback';
    const qrsRaw = localStorage.getItem('braise:demo_sample_qrs_v2');
    const parsedQrs = parseJSON<DemoQR[]>(qrsRaw, 'demoTables sample_qrs');
    if (parsedQrs && parsedQrs.length > 0) {
      const list = parsedQrs
        .filter((qr) => qr.table_number)
        .map((qr) => ({
          id: qr.qr_slug || qr.table_id || qr.table_number || '',
          number: String(qr.table_number ?? ''),
          qr_slug: qr.qr_slug,
        }))
        .filter((entry) => entry.id && entry.number);
      if (list.length > 0) {
        src = 'sample_qrs';
        return { list, src };
      }
    }
    if (dbDemoTables.length > 0) {
      src = 'db';
      return { list: dbDemoTables.map((t) => ({ id: t.id, number: String(t.table_number), qr_slug: t.qr_slug })), src };
    }
    // Fallback synthesized 12 tables
    const list = Array.from({ length: 12 }, (_, i) => ({ id: `table-${i + 1}`, number: String(i + 1) }));
    return { list, src };
  }, [dbDemoTables]);
  const demoTables = demoTablesMemo.list;
  useEffect(() => {
    if (import.meta.env?.DEV && !demoTablesLogged) {
      console.debug(`[useDemoV2] demoTables source=${demoTablesMemo.src} count=`, demoTables.length);
      setDemoTablesLogged(true);
    }
  }, [demoTablesLogged, demoTablesMemo, demoTables.length]);

  const refreshDemo = useCallback(async () => { loadDemoSession(); }, [loadDemoSession]);

  const applyProvisionedSession = useCallback(
    (session: ProvisionedDemoSession, fallbackName: string) => {
      const restaurantLabel = isNonEmptyString(session.name) ? session.name : fallbackName || 'Demo Restaurant';

      persistString(DEMO_STORAGE_KEYS.JWT, session.demo_jwt);
      persistString(DEMO_STORAGE_KEYS.RESTAURANT_ID, session.demo_restaurant_id);
      persistString(DEMO_STORAGE_KEYS.SLUG, session.slug);
      persistString(DEMO_STORAGE_KEYS.EXPIRES_AT, session.expires_at);
      persistString(DEMO_STORAGE_KEYS.RESTAURANT_NAME, restaurantLabel);

      const sampleList =
        session.sample_qrs && session.sample_qrs.length > 0
          ? session.sample_qrs
          : buildDefaultSampleQRs(session.slug);

      if (sampleList && sampleList.length > 0) {
        safeSet(DEMO_STORAGE_KEYS.SAMPLE_QRS, sampleList);
      } else {
        safeRemove(DEMO_STORAGE_KEYS.SAMPLE_QRS);
      }

      setSampleQRs(sampleList ?? []);
      setIsDemo(true);
      setDemoJWT(session.demo_jwt);
      setDemoRestaurantId(session.demo_restaurant_id);
      setSlug(session.slug);
      setExpiresAt(session.expires_at);
      setRestaurantName(restaurantLabel);

      const expirationMs = Date.parse(session.expires_at);
      if (!Number.isNaN(expirationMs)) {
        setTimeLeft(Math.max(0, Math.floor((expirationMs - Date.now()) / 1000)));
      } else {
        setTimeLeft(0);
      }
    },
    [setDemoJWT, setDemoRestaurantId, setExpiresAt, setIsDemo, setRestaurantName, setSampleQRs, setSlug, setTimeLeft],
  );

  const createDemo = useCallback(
    async (name: string, desiredSlug: string, locationName?: string) => {
      const trimmedName = (typeof name === 'string' && name.trim().length > 0) ? name.trim() : 'Demo Restaurant';
      const slugSource = (typeof desiredSlug === 'string' && desiredSlug.trim().length > 0) ? desiredSlug.trim() : trimmedName;
      const normalizedSlug = slugSource
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        .substring(0, 30);
      const locationLabel = typeof locationName === 'string' ? locationName.trim() : '';

      setIsLoading(true);
      try {
        const response = await apiFetch(DEMO_PROVISION_ENDPOINT, {
          method: 'POST',
          body: JSON.stringify({
            name: trimmedName,
            slug: normalizedSlug,
            locationName: locationLabel || undefined,
          }),
        });

        let payload: unknown = null;
        try {
          payload = await response.json();
        } catch (error) {
          logDevError('demo provision parse payload', error);
        }

        const session = normalizeProvisionPayload(payload);
        const payloadOk =
          payload && typeof payload === 'object' && 'ok' in (payload as Record<string, unknown>)
            ? Boolean((payload as { ok?: unknown }).ok)
            : response.ok;

        if (!payloadOk || !session) {
          const errorMessage =
            deriveProvisionErrorMessage(payload) ??
            (response.ok
              ? 'We could not create the demo environment. Please retry shortly.'
              : `Demo provisioning failed (${response.status})`);
          logDevError('demo provision rejected', errorMessage);
          return { success: false, error: errorMessage };
        }

        applyProvisionedSession(session, trimmedName);

        const restaurantLabel = isNonEmptyString(session.name) ? session.name : trimmedName;
        toast({
          title: 'Demo Created!',
          description: `Your demo restaurant "${restaurantLabel}" is ready!`,
        });

        return { success: true, data: session as unknown as Record<string, unknown> };
      } catch (error: unknown) {
        logDevError('demo provision request failed', error);
        const message =
          error instanceof Error ? error.message : 'Unexpected error occurred while creating the demo.';
        return { success: false, error: message };
      } finally {
        setIsLoading(false);
      }
    },
    [applyProvisionedSession, toast],
  );

  const endDemo = useCallback(async () => {
    Object.values(DEMO_STORAGE_KEYS).forEach((k) => safeRemove(k));
    safeRemove(ORDERS_V2);
    safeRemove(ORDERS_LEGACY);
    safeRemove(SEEDED_FLAG);
    safeRemove(SESSION_ID);
    initialDbHandledRef.current = false;
    keepSeedsRef.current = false;
    topUpSeedsRef.current = false;
    dbEmptyLoggedRef.current = false;
    topUpLoggedRef.current = false;
    seededOnceRef.current = false;
    ordersRef.current = [];
    setOrders([]);
    window.dispatchEvent(new CustomEvent('demoOrdersUpdated', { detail: { orders: [] } }));
    if (import.meta.env?.DEV) console.debug('[demo] cleared local demo data');
    setIsDemo(false);
  }, []);

  const updateDemoOrderStatus = useCallback(async (orderId: string, newStatus: string) => {
    const nowIso = new Date().toISOString();
    const client = createDemoAwareClient(demoJWT || undefined);
    try {
      if (demoRestaurantId) {
        const { error } = await client
          .from('demo_orders')
          .update({ status: newStatus, updated_at: nowIso })
          .eq('id', orderId)
          .eq('demo_restaurant_id', demoRestaurantId);
        if (error) throw error;
        return;
      }
      throw new Error('No demo_restaurant_id');
    } catch (error) {
      logDevError('updateDemoOrderStatus fallback', error);
      const keyV2 = ORDERS_V2;
      const keyLegacy = ORDERS_LEGACY;
      const raw = localStorage.getItem(keyV2) || localStorage.getItem(keyLegacy);
      const parsed = parseJSON<DemoOrder[]>(raw, 'orders fallback update') || [];
      const updated = parsed.map((order) =>
        order.id === orderId ? { ...order, status: newStatus, updated_at: nowIso } : order
      );
      safeSet(keyV2, updated);
      safeSet(keyLegacy, updated);
      setOrders(updated);
      window.dispatchEvent(new CustomEvent('demoOrdersUpdated', { detail: { orders: updated } }));
    }
  }, [demoJWT, demoRestaurantId]);

  // Create counter order (truly optimistic and resilient)
  const createCounterOrder = useCallback(async (draft: {
    tableNumber?: string;
    table_number?: string;
    items: Array<{ 
      id?: string; 
      name?: string; 
      name_snapshot?: string;
      price_cents?: number; 
      price_snapshot_cents?: number;
      price?: number;
      qty?: number;
      quantity?: number;
      notes?: string;
      modifiers?: Array<{ name: string; price_delta_cents?: number }> 
    }>;
    guests?: number;
    notes?: string;
  }): Promise<{ id: string; code: string }> => {
    // Demo path only
    if (!isDemo) {
      throw new Error('createCounterOrder only works in demo mode');
    }

    const nowIso = new Date().toISOString();
    const tableLabel = String(draft.tableNumber || draft.table_number || '1');

    // Build optimistic order items with flexible fields
    const orderItems = (draft.items || []).map((item) => ({
      id: `oi-${Math.random().toString(36).substring(2)}`,
      name_snapshot: item.name || item.name_snapshot || 'Unknown Item',
      price_snapshot_cents: item.price_snapshot_cents ?? item.price_cents ?? Math.round((item.price || 0) * 100),
      quantity: Number(item.quantity || item.qty || 1),
      notes: item.notes || ''
    }));

    // Totals in cents
    const subtotal_cents = orderItems.reduce((sum, item) => {
      return sum + (item.price_snapshot_cents * item.quantity);
    }, 0);
    const tax_cents = 0;
    const total_cents = subtotal_cents + tax_cents;

    const optimisticOrder: DemoOrder = {
      id: `optimistic-${Math.random().toString(36).substring(2)}`,
      code: `ORD-${Date.now()}`,
      order_code: `ORD-${Date.now()}`,
      status: 'PENDING_PAYMENT',
      placed_at: nowIso,
      restaurant_id: demoRestaurantId || '',
      table_number: tableLabel,
      order_items: orderItems,
      items: orderItems,
      subtotal_cents,
      tax_cents,
      total_cents,
      total_amount: total_cents / 100,
      payment_method: 'CARD_TEST',
      notes: draft.notes || '',
      created_at: nowIso,
      updated_at: nowIso,
      source: 'COUNTER',
    };

    // Immediately update state
    setOrders(prev => {
      const updated = [optimisticOrder, ...prev];
      // Mirror to both LS keys
      safeSet(ORDERS_V2, updated);
      safeSet(ORDERS_LEGACY, updated);
      // Dispatch event
      window.dispatchEvent(new CustomEvent('demoOrdersUpdated', { detail: { orders: updated } }));
      // Update ref
      ordersRef.current = updated;
      return updated;
    });

    // Dev log
    if (import.meta.env?.DEV) {
      console.debug('[createCounterOrder] optimistic append', optimisticOrder.code);
    }

    // Return immediately (don't await DB)
    const result = { id: optimisticOrder.id, code: optimisticOrder.code };

    // Fire DB insert in background (demo-aware client with x-demo-token)
    if (demoRestaurantId && demoJWT) {
      try {
        const client = createDemoAwareClient(demoJWT);
        const { data: locData } = await client
          .from('demo_locations')
          .select('id')
          .eq('demo_restaurant_id', demoRestaurantId)
          .limit(1)
          .single();
        const { data: tblData } = await client
          .from('demo_tables')
          .select('id')
          .eq('demo_restaurant_id', demoRestaurantId)
          .eq('table_number', tableLabel)
          .limit(1)
          .maybeSingle();

        const insertOrder = {
          demo_restaurant_id: demoRestaurantId,
          table_id: tblData?.id || null,
          location_id: locData?.id || null,
          order_code: optimisticOrder.code,
          status: 'PENDING_PAYMENT',
          guest_count: draft.guests ?? 1,
          subtotal_cents,
          tax_cents,
          service_charge_cents: 0,
          tip_cents: 0,
          total_cents,
          notes: draft.notes || null,
          placed_at: nowIso,
          source: 'COUNTER',
        };

        const { data: orderRows, error: orderErr } = await client
          .from('demo_orders')
          .insert(insertOrder)
          .select('id, order_code')
          .limit(1);
        
        if (orderErr) throw orderErr;
        const orderId = orderRows?.[0]?.id as string;

        if (orderId) {
          const itemsRows = orderItems.map((item) => ({
            order_id: orderId,
            item_id: null,
            quantity: item.quantity,
            price_snapshot_cents: item.price_snapshot_cents,
            name_snapshot: item.name_snapshot,
            modifiers_json: JSON.stringify([]),
          }));
          await client.from('demo_order_items').insert(itemsRows);
        }

        // On success: find optimistic entry by code, replace with DB row
        setOrders(prev => {
          const updated = prev.map(order => 
            order.order_code === optimisticOrder.code 
              ? { 
                  ...order, 
                  id: orderId, // Update with real DB ID
                }
              : order
          );
          // Update both LS mirrors
          safeSet(ORDERS_V2, updated);
          safeSet(ORDERS_LEGACY, updated);
          // Re-dispatch event
          window.dispatchEvent(new CustomEvent('demoOrdersUpdated', { detail: { orders: updated } }));
          // Update ref
          ordersRef.current = updated;
          return updated;
        });

        // Dev log
        if (import.meta.env?.DEV) {
          console.debug('[createCounterOrder] DB success - reconciled by code', optimisticOrder.code);
        }

      } catch {
        // On any error: keep optimistic entry
        if (import.meta.env?.DEV) {
          console.warn('[createCounterOrder] DB failed - kept optimistic', optimisticOrder.code);
        }
      }
    }

    return result;
  }, [isDemo, demoRestaurantId, demoJWT]);
  return {
    isDemo,
    demoRestaurantId,
    slug,
    demoJWT,
    demoToken: demoJWT,
    expiresAt,
    restaurantName,
    isLoading,
    timeLeft,
    timeLeftFormatted,
    isExpired,
    orders,
    menuItems,
    ready,
    orderCount,
    todayOrdersCount,
    revenueCents,
    kitchenQueue,
    demoTables,
    createDemo,
    endDemo,
    refreshDemo,
    updateDemoOrderStatus,
    createCounterOrder,
    sampleQRs,
  };
}
